import React, { useState, useEffect, useCallback, useRef } from "react";
import { BookOpen, Search, ThumbsUp, ThumbsDown, Eye, ArrowLeft, Tag, ChevronRight } from "lucide-react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import * as api from "../../lib/api";
import { Input } from "../ui/input";
import { getTicketTypeConfig } from "../../lib/utils";
import { toast } from "sonner";

const TICKET_TYPES_FOR_FILTER = [
    "incident", "problem", "password_reset", "software_install", "access_request",
    "new_equipment", "onboarding", "offboarding", "security", "question",
    "defective_equipment", "equipment_loan",
];

export default function PublicKnowledgeBase() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filterType, setFilterType] = useState("");
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [voted, setVoted] = useState({});
    const searchTimerRef = useRef(null);

    const loadArticles = useCallback(async () => {
        try {
            setLoading(true);
            const data = await api.searchKBArticles(search, filterType || undefined);
            setArticles(data);
        } catch {
            toast.error("Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    }, [search, filterType]);

    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(loadArticles, 300);
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, [loadArticles]);

    const handleVote = async (articleId, helpful) => {
        if (voted[articleId]) return;
        try {
            await api.voteKBArticle(articleId, helpful);
            setVoted((prev) => ({ ...prev, [articleId]: helpful ? "up" : "down" }));
            toast.success("Merci pour votre retour !");
            if (selectedArticle?.id === articleId) {
                setSelectedArticle((prev) => ({
                    ...prev,
                    [helpful ? "helpful_count" : "not_helpful_count"]: (prev[helpful ? "helpful_count" : "not_helpful_count"] || 0) + 1,
                }));
            }
        } catch {
            toast.error("Erreur");
        }
    };

    const openArticle = async (id) => {
        try {
            const data = await api.getKBArticle(id);
            setSelectedArticle(data);
        } catch {
            toast.error("Erreur lors du chargement de l'article");
        }
    };

    if (selectedArticle) {
        return (
            <div className="max-w-3xl" data-testid="kb-article-detail">
                <button
                    onClick={() => setSelectedArticle(null)}
                    className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Retour aux articles
                </button>

                <article>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="kb-article-title">
                        {selectedArticle.title}
                    </h1>
                    {selectedArticle.summary && (
                        <p className="text-sm text-gray-500 mb-4">{selectedArticle.summary}</p>
                    )}

                    <div className="flex items-center gap-3 mb-6 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {selectedArticle.views} vue{selectedArticle.views !== 1 ? "s" : ""}
                        </span>
                        {(selectedArticle.tags || []).length > 0 && (
                            <span className="flex items-center gap-1">
                                <Tag className="w-3 h-3" />
                                {selectedArticle.tags.join(", ")}
                            </span>
                        )}
                        {(selectedArticle.related_ticket_types || []).length > 0 && (
                            <div className="flex gap-1">
                                {selectedArticle.related_ticket_types.map((t) => (
                                    <span key={t} className="px-1.5 py-0 text-[9px] font-medium rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                                        {getTicketTypeConfig(t).label}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(selectedArticle.content || "")) }}
                        data-testid="kb-article-content"
                    />

                    {/* Vote */}
                    <div className="mt-8 p-4 bg-gray-50 rounded-lg border text-center" data-testid="kb-article-vote">
                        <p className="text-sm font-medium text-gray-700 mb-3">Cet article vous a aidé ?</p>
                        <div className="flex items-center justify-center gap-3">
                            <button
                                onClick={() => handleVote(selectedArticle.id, true)}
                                disabled={!!voted[selectedArticle.id]}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${voted[selectedArticle.id] === "up" ? "bg-green-100 text-green-700 border border-green-200" : "bg-white border hover:bg-green-50 hover:border-green-200 text-gray-600"} disabled:opacity-60`}
                                data-testid="kb-vote-up"
                            >
                                <ThumbsUp className="w-4 h-4" />
                                Oui ({selectedArticle.helpful_count || 0})
                            </button>
                            <button
                                onClick={() => handleVote(selectedArticle.id, false)}
                                disabled={!!voted[selectedArticle.id]}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${voted[selectedArticle.id] === "down" ? "bg-red-100 text-red-700 border border-red-200" : "bg-white border hover:bg-red-50 hover:border-red-200 text-gray-600"} disabled:opacity-60`}
                                data-testid="kb-vote-down"
                            >
                                <ThumbsDown className="w-4 h-4" />
                                Non ({selectedArticle.not_helpful_count || 0})
                            </button>
                        </div>
                    </div>
                </article>
            </div>
        );
    }

    return (
        <div data-testid="public-kb-page">
            <div className="page-header mb-6">
                <h1 className="page-title flex items-center gap-2">
                    <BookOpen className="w-6 h-6" />
                    Base de connaissances
                </h1>
                <p className="page-subtitle">Trouvez des réponses à vos questions fréquentes</p>
            </div>

            {/* Search + filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
                <div className="relative flex-1 w-full max-w-lg">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher un article..."
                        className="pl-9 h-10"
                        data-testid="public-kb-search"
                    />
                </div>
                <div className="flex gap-1 flex-wrap">
                    <button
                        onClick={() => setFilterType("")}
                        className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${!filterType ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"}`}
                    >
                        Tous
                    </button>
                    {TICKET_TYPES_FOR_FILTER.map((t) => {
                        const cfg = getTicketTypeConfig(t);
                        return (
                            <button
                                key={t}
                                onClick={() => setFilterType(filterType === t ? "" : t)}
                                className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${filterType === t ? "bg-blue-50 text-blue-700 border border-blue-200" : "text-gray-500 hover:bg-gray-100"}`}
                                data-testid={`public-kb-filter-${t}`}
                            >
                                {cfg.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Articles grid */}
            {loading ? (
                <div className="text-center py-12 text-gray-400">Chargement...</div>
            ) : articles.length === 0 ? (
                <div className="text-center py-12">
                    <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Aucun article trouvé</p>
                    {search && <p className="text-xs text-gray-400 mt-1">Essayez d'autres termes de recherche</p>}
                </div>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {articles.map((article) => (
                        <button
                            key={article.id}
                            onClick={() => openArticle(article.id)}
                            className="card text-left hover:border-blue-200 hover:shadow-md transition-all group"
                            data-testid={`public-kb-article-${article.id}`}
                        >
                            <div className="card-body">
                                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-1 line-clamp-2">
                                    {article.title}
                                </h3>
                                {article.summary && (
                                    <p className="text-xs text-gray-500 line-clamp-2 mb-2">{article.summary}</p>
                                )}
                                <div className="flex items-center justify-between text-[10px] text-gray-400">
                                    <div className="flex items-center gap-2">
                                        <span>{article.views} vue{article.views !== 1 ? "s" : ""}</span>
                                        {article.helpful_count > 0 && (
                                            <span className="text-green-600">{article.helpful_count} utile{article.helpful_count !== 1 ? "s" : ""}</span>
                                        )}
                                    </div>
                                    <ChevronRight className="w-3 h-3 text-gray-300 group-hover:text-blue-400" />
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
